import { reflect } from '@effector/reflect';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Coins } from 'lucide-react';
import { not } from 'patronum';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { shiftModel } from 'entities/shift';
import { $t, breakpoints, translated } from 'shared/lib';
import { Calculator, createModalForm, ModalFormProps } from 'shared/ui';
import { DepositPlayerModel } from '../../model';

export const createDepositPlayerModal = (model: DepositPlayerModel) => {
	const ModalForm = createModalForm(model);

	const AmountInput = reflect({
		view: Calculator,
		bind: {
			'data-test-id': 'deposit-player-amount-input',
			size: breakpoints.md.map((md) => (md ? 'md' : 'sm')),
			onChange: (e) => model.form.fields.amount.change(e.toString()),
			value: model.form.fields.amount.$value,
			numberInputProps: combine({
				label: translated('deposit-player.amount'),
				placeholder: '250.00',
				rightSection: shiftModel.$shiftTotal.map((shift) => shift?.currency ?? null),
				errorProps: { 'data-test-id': 'deposit-player-amount-input-error' },
				error: combine(
					[model.form.fields.amount.$errors, $t],
					([errors, t]) =>
						errors.length > 0 &&
						t(`errors:validation.${errors[0]}`, {
							fieldName: t('deposit-player.amount'),
						}),
				),
			}),
		},
	});

	const DepositPlayerModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [pending, md] = useUnit([model.$pending, breakpoints.md]);

		return (
			<ModalForm
				ref={ref}
				title={t('deposit-player.title')}
				submitButtonProps={{
					children: t('deposit-player.deposit'),
					leftSection: <Coins />,
					loading: pending,
					fullWidth: true,
					size: md ? 'xl' : 'lg',
				}}
				size={md ? '2xl' : 'xl'}
				{...props}
			>
				<AmountInput />
			</ModalForm>
		);
	});

	const DepositPlayerModalButton = reflect({
		view: ModalForm.Button,
		bind: {
			'data-test-id': 'deposit-player-button',
			leftSection: <Coins />,
			children: translated('deposit-player.deposit'),
			size: breakpoints.md.map((md) => (md ? 'lg' : 'md')),
			fullWidth: not(breakpoints.sm),
		},
	});

	return Object.assign(DepositPlayerModal, {
		Button: DepositPlayerModalButton,
	});
};
